/**
 * VoiceProfileEditor — edit a voice profile with live markdown preview.
 *
 * S-064 / FEAT-006 (Phase 5). FEAT-052 / S-298 re-keyed this from a
 * publication's ID to the voice profile's own ID — a voice profile is a
 * shared, independent entity, not 1:1 with a publication. Route:
 * /settings/voice/:voiceProfileId. Standalone creation lives at its own
 * route, /settings/voice/new (S-299, VoiceProfileCreateView below).
 *
 * What it does:
 *   - Reads the voice profile directly via GET /voice-profiles/:id, plus
 *     a reverse lookup (GET /publications?voice_profile_id=:id) to show
 *     which publications reference it ("Used by") and gate delete.
 *   - Left column: form with `name` (text), `core_identity` (markdown
 *     textarea), `hard_rules` (repeating list editor), `litmus_test`
 *     (short text). A collapsible "Advanced" disclosure exposes the
 *     other rule arrays (`strong_tendencies`, `light_preferences`,
 *     `anti_ai_patterns`, `top_3_rules`) which the API supports today
 *     but most users never touch.
 *   - Right column: live preview rendered through lib/markdown.ts (no
 *     WYSIWYG library — per memory [[feedback-no-wysiwyg]] and
 *     [[feedback-markdown-must-parse]]). The preview shows `core_identity`
 *     as HTML and `hard_rules` as a bulleted list beneath it.
 *   - Save: PATCH /voice-profiles/:id via useApiMutation. Sticky save
 *     bar at the bottom with dirty-state tracking — Save button is
 *     disabled when no changes; navigation triggers a beforeunload
 *     warning while dirty.
 *
 * Schema mapping (db/schema.ts → API):
 *   - name (text) — required, trimmed name (1-120 chars)
 *   - core_identity (text) — markdown body, longContent cap 20k
 *   - hard_rules (jsonb array) — exposed as list editor; per-rule cap 500
 *   - litmus_test (text|null) — shortText cap
 *   - strong_tendencies / light_preferences / anti_ai_patterns (jsonb arrays)
 *   - top_3_rules (text array, max 3) — exposed in Advanced
 *   - openings_by_content_type, closings_by_content_type, tone_interactions
 *     (jsonb maps) — NOT exposed in v1; require shape-aware UI we don't
 *     have yet. Left untouched on save (PATCH is partial).
 *
 * RBAC: route reachable only via the admin-gated Settings nav. The voice-
 * profiles routes are open to all workspace members but the surrounding
 * gating is admin-only.
 */

import { useEffect, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi, useApiMutation, useApiQuery } from "@/lib/api";
import { useEnsureActiveWorkspace } from "@/lib/useActiveWorkspace";
import { markdownToHtml } from "@/lib/markdown";
import {
  AnalyzeVoiceDocumentModal,
  type AnalyzedVoiceFields,
} from "@/components/AnalyzeVoiceDocumentModal";
import { useSettingsWorkspaces } from "@/lib/useSettingsWorkspaces";

// FEAT-052 — minimal shape for the "Used by" list. Deliberately not the
// full Publication interface PublicationsView.tsx has — this file only
// ever displays the name, same small-type-duplication convention as
// VoiceProfile below.
interface UsedByPublication {
  id: number;
  name: string;
}

interface UsedByResponse {
  data: UsedByPublication[];
}

// FEAT-054 / S-305 — matches the API contract's Finding model
// (POST /voice-profiles/validate).
export interface ValidationFinding {
  field: "hard_rules" | "anti_ai_patterns" | "light_preferences";
  item: string;
  category: "miscategorized" | "near_duplicate" | "process_only" | "system_redundant";
  explanation: string;
  suggestion?: string | null;
  related_item?: { field: string; item: string } | null;
}

interface VoiceProfile {
  id: number;
  name: string;
  coreIdentity?: string;
  core_identity?: string;
  hardRules?: string[];
  hard_rules?: string[];
  strongTendencies?: string[];
  strong_tendencies?: string[];
  lightPreferences?: string[];
  light_preferences?: string[];
  antiAiPatterns?: string[];
  anti_ai_patterns?: string[];
  top3Rules?: string[];
  top_3_rules?: string[];
  litmusTest?: string | null;
  litmus_test?: string | null;
  // FEAT-008 / S-121 — markdown columns added in migration 0016
  sourceMarkdown?: string | null;
  source_markdown?: string | null;
}

interface UpdateBody {
  name?: string;
  core_identity?: string;
  hard_rules?: string[];
  strong_tendencies?: string[];
  light_preferences?: string[];
  anti_ai_patterns?: string[];
  top_3_rules?: string[];
  litmus_test?: string | null;
  // FEAT-008 / S-121
  source_markdown?: string | null;
}

function readField<T>(
  row: VoiceProfile,
  camel: keyof VoiceProfile,
  snake: keyof VoiceProfile,
  fallback: T
): T {
  const v = (row[camel] ?? row[snake]) as T | undefined;
  return (v ?? fallback) as T;
}

interface FormState {
  name: string;
  coreIdentity: string;
  hardRules: string[];
  strongTendencies: string[];
  lightPreferences: string[];
  antiAiPatterns: string[];
  top3Rules: string[];
  litmusTest: string;
  // FEAT-008 / S-121
  sourceMarkdown: string;
}

function rowToForm(row: VoiceProfile): FormState {
  return {
    name: row.name ?? "",
    coreIdentity: readField<string>(row, "coreIdentity", "core_identity", ""),
    hardRules: readField<string[]>(row, "hardRules", "hard_rules", []),
    strongTendencies: readField<string[]>(
      row,
      "strongTendencies",
      "strong_tendencies",
      []
    ),
    lightPreferences: readField<string[]>(
      row,
      "lightPreferences",
      "light_preferences",
      []
    ),
    antiAiPatterns: readField<string[]>(
      row,
      "antiAiPatterns",
      "anti_ai_patterns",
      []
    ),
    top3Rules: readField<string[]>(row, "top3Rules", "top_3_rules", []),
    litmusTest: readField<string>(row, "litmusTest", "litmus_test", "") ?? "",
    // FEAT-008 / S-121
    sourceMarkdown:
      readField<string | null>(row, "sourceMarkdown", "source_markdown", null) ?? "",
  };
}

// FEAT-051 / S-297, re-homed to VoiceProfileCreateView by FEAT-052 / S-299
// — blank FormState for the standalone create flow.
function emptyForm(name: string): FormState {
  return {
    name,
    coreIdentity: "",
    hardRules: [],
    strongTendencies: [],
    lightPreferences: [],
    antiAiPatterns: [],
    top3Rules: [],
    litmusTest: "",
    sourceMarkdown: "",
  };
}

// FEAT-051 — shared by VoiceEditorBody (enrich, S-296) and
// VoiceProfileCreateView (create, S-299): merges an analyzer result into
// an existing FormState. Pure — no state, no side effects — so both
// callers stay in charge of their own setForm/setAnalyzedFrom calls.
function mergeAnalyzedFields(
  form: FormState,
  fields: AnalyzedVoiceFields
): FormState {
  return {
    ...form,
    coreIdentity: fields.core_identity,
    hardRules: fields.hard_rules,
    strongTendencies: fields.strong_tendencies,
    lightPreferences: fields.light_preferences,
    antiAiPatterns: fields.anti_ai_patterns,
    top3Rules: fields.top_3_rules,
    litmusTest: fields.litmus_test ?? "",
    sourceMarkdown: fields.source_markdown,
  };
}

// FEAT-054 / S-305 — shared by VoiceEditorBody and VoiceProfileCreateView.
// POSTs the 3 fields the validator cares about, filtered with the same
// convention every save/create payload in this file already uses.
async function checkForIssues(
  api: ReturnType<typeof useApi>,
  form: FormState
): Promise<ValidationFinding[]> {
  const result = await api<{ findings: ValidationFinding[] }>("/voice-profiles/validate", {
    method: "POST",
    body: JSON.stringify({
      hard_rules: form.hardRules.filter((r) => r.trim().length > 0),
      anti_ai_patterns: form.antiAiPatterns.filter((r) => r.trim().length > 0),
      light_preferences: form.lightPreferences.filter((r) => r.trim().length > 0),
    }),
  });
  return result.findings;
}

function checkErrorMessage(e: unknown): string {
  if (e instanceof ApiError && e.status === 502) {
    return "Could not check this profile right now — try again in a moment.";
  }
  return e instanceof Error ? e.message : "Check failed.";
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// ---------- Top-level view ----------

// FEAT-052 / S-298 — keyed by the voice profile's own ID, not a
// publication's. A missing/invalid ID is just a 404 now; standalone
// creation lives at its own route (/settings/voice/new, S-299).
export function VoiceProfileEditor() {
  const { voiceProfileId } = useParams();
  const vpId = Number(voiceProfileId);

  const voiceQ = useApiQuery<VoiceProfile>(
    ["voice-profile", vpId],
    `/voice-profiles/${vpId}`,
    { enabled: Number.isFinite(vpId) }
  );

  if (!Number.isFinite(vpId)) {
    return (
        <div className="settings-page">
          <div className="state-error">Invalid voice profile id.</div>
        </div>
    );
  }

  if (voiceQ.isLoading) {
    return (
        <div className="settings-page">
          <div className="state-loading">Loading voice profile…</div>
        </div>
    );
  }

  if (voiceQ.error) {
    return (
        <div className="settings-page">
          <div className="state-error">
            {voiceQ.error instanceof ApiError && voiceQ.error.status === 404
              ? "Voice profile not found."
              : (voiceQ.error.message ?? "Could not load voice profile.")}
          </div>
        </div>
    );
  }

  if (!voiceQ.data) return null;

  return <VoiceEditorBody voice={voiceQ.data} />;
}

// ---------- Editor body ----------

interface BodyProps {
  voice: VoiceProfile;
}

function VoiceEditorBody({ voice }: BodyProps) {
  const qc = useQueryClient();
  const initial = useMemo(() => rowToForm(voice), [voice]);
  const [form, setForm] = useState<FormState>(initial);
  // Re-sync local form state if the server returns an updated row
  // (e.g., after Save).
  useEffect(() => {
    setForm(rowToForm(voice));
  }, [voice]);

  // FEAT-051 / S-296 — "Analyze a document" populates the unsaved form
  // state exactly like a manual edit would; the existing dirty-check and
  // Save flow below need no changes to pick this up.
  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const [analyzedFrom, setAnalyzedFrom] = useState<string | null>(null);

  function applyAnalyzedFields(fields: AnalyzedVoiceFields, sourceLabel: string) {
    setForm((f) => mergeAnalyzedFields(f, fields));
    setAnalyzedFrom(sourceLabel);
  }

  const [savedAt, setSavedAt] = useState<Date | null>(null);
  // Phase 5 UX polish (2026-05-25): transient inline "Saved" badge
  // next to the Save button. Set on save success, auto-cleared
  // after 2.5s so the user gets immediate visual confirmation
  // (the sticky save bar status changes too, but the inline pill
  // is harder to miss).
  const [recentlySaved, setRecentlySaved] = useState(false);

  const dirty =
    form.name !== initial.name ||
    form.coreIdentity !== initial.coreIdentity ||
    form.litmusTest !== initial.litmusTest ||
    form.sourceMarkdown !== initial.sourceMarkdown ||
    !arraysEqual(form.hardRules, initial.hardRules) ||
    !arraysEqual(form.strongTendencies, initial.strongTendencies) ||
    !arraysEqual(form.lightPreferences, initial.lightPreferences) ||
    !arraysEqual(form.antiAiPatterns, initial.antiAiPatterns) ||
    !arraysEqual(form.top3Rules, initial.top3Rules);

  // beforeunload guard — prevent accidental navigation while dirty.
  useEffect(() => {
    if (!dirty) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const mutation = useApiMutation<VoiceProfile, UpdateBody>(
    `/voice-profiles/${voice.id}`,
    "PATCH",
    {
      onSuccess: () => {
        setSavedAt(new Date());
        setRecentlySaved(true);
        setAnalyzedFrom(null);
        // Phase 5 UX polish (2026-05-25): invalidate the voice-profile
        // query so `voice` (and therefore `initial` via useMemo)
        // recomputes with the just-saved values. Without this, `dirty`
        // stays true after save (current `form` !== stale `initial`)
        // and the "Unsaved changes" indicator never clears.
        void qc.invalidateQueries({ queryKey: ["voice-profile", voice.id] });
      },
    }
  );

  // Auto-clear the inline "Saved" badge after a short delay.
  useEffect(() => {
    if (!recentlySaved) return;
    const t = window.setTimeout(() => setRecentlySaved(false), 2500);
    return () => window.clearTimeout(t);
  }, [recentlySaved]);

  // FEAT-052 / S-298 — voice profiles are independent entities now; this
  // is the read-only reverse lookup of which publications reference this
  // one. Reassignment stays in the Publications editor's own picker.
  const navigate = useNavigate();
  const api = useApi();
  const usedByQ = useApiQuery<UsedByResponse>(
    ["publications", "voice-profile", voice.id],
    `/publications?voice_profile_id=${voice.id}`
  );
  const usedBy = usedByQ.data?.data ?? [];

  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // FEAT-054 / S-305 — "Check for issues." findings === null means no
  // check has run yet (ValidationFindingsPanel renders nothing in that
  // state); [] means a check ran clean.
  const [checking, setChecking] = useState(false);
  const [findings, setFindings] = useState<ValidationFinding[] | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);

  async function handleCheck() {
    if (checking) return;
    setCheckError(null);
    setChecking(true);
    try {
      setFindings(await checkForIssues(api, form));
    } catch (e) {
      setCheckError(checkErrorMessage(e));
    } finally {
      setChecking(false);
    }
  }

  async function handleDelete() {
    if (usedBy.length > 0 || deleting) return;
    if (!window.confirm(`Delete "${voice.name}"? This cannot be undone.`)) return;
    setDeleteError(null);
    setDeleting(true);
    try {
      await api(`/voice-profiles/${voice.id}`, { method: "DELETE" });
      navigate("/settings/voice");
    } catch (e) {
      setDeleteError(
        e instanceof ApiError && e.status === 409
          ? "This voice profile is still referenced by a publication and can't be deleted."
          : e instanceof Error
            ? e.message
            : "Delete failed."
      );
      setDeleting(false);
    }
  }

  function buildPatch(): UpdateBody {
    const body: UpdateBody = {};
    if (form.name.trim() !== initial.name) body.name = form.name.trim();
    if (form.coreIdentity !== initial.coreIdentity) {
      body.core_identity = form.coreIdentity;
    }
    if (form.litmusTest !== initial.litmusTest) {
      body.litmus_test = form.litmusTest.trim() || null;
    }
    if (!arraysEqual(form.hardRules, initial.hardRules)) {
      body.hard_rules = form.hardRules.filter((r) => r.trim().length > 0);
    }
    if (!arraysEqual(form.strongTendencies, initial.strongTendencies)) {
      body.strong_tendencies = form.strongTendencies.filter(
        (r) => r.trim().length > 0
      );
    }
    if (!arraysEqual(form.lightPreferences, initial.lightPreferences)) {
      body.light_preferences = form.lightPreferences.filter(
        (r) => r.trim().length > 0
      );
    }
    if (!arraysEqual(form.antiAiPatterns, initial.antiAiPatterns)) {
      body.anti_ai_patterns = form.antiAiPatterns.filter(
        (r) => r.trim().length > 0
      );
    }
    if (!arraysEqual(form.top3Rules, initial.top3Rules)) {
      body.top_3_rules = form.top3Rules.filter((r) => r.trim().length > 0);
    }
    if (form.sourceMarkdown !== initial.sourceMarkdown) {
      body.source_markdown = form.sourceMarkdown.trim() || null;
    }
    return body;
  }

  function handleSave() {
    if (!dirty || mutation.isPending) return;
    mutation.mutate(buildPatch());
  }

  return (
      <div className="settings-page editor-page">
        <header className="editor-header">
          {/* Phase 5 UX polish (2026-05-25): explicit Back link in
              addition to the breadcrumb. The muted breadcrumb wasn't
              reading as navigable to users during browser smoke; this
              gives an unmistakable exit. */}
          <Link to="/settings/voice" className="editor-back-link">
            ← Back to voice profiles
          </Link>
          <nav className="editor-breadcrumb" aria-label="Breadcrumb">
            <Link to="/settings/voice">Settings · Voice Profiles</Link>
            <span className="editor-breadcrumb-sep">/</span>
            <span>{voice.name}</span>
          </nav>
          <h1 className="editor-title">
            Voice profile — {voice.name}
          </h1>
          <p className="editor-sub">
            Markdown is supported in the core identity. Hard rules are
            inlined into agent prompts; keep each rule short.
          </p>
          {/* FEAT-051 / S-296. FEAT-052 / S-300 — restyled from a
              ghost link to a primary action; the founder had trouble
              finding this as a clickable upload affordance. FEAT-054 /
              S-305 — "Check for issues" sits next to it; the wrapping row
              keeps both button-sized instead of stretching full-width
              under editor-header's flex-column. */}
          <div style={{ display: "flex", gap: 12, alignSelf: "flex-start" }}>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setAnalyzeOpen(true)}
            >
              Upload a document to analyze
            </button>
            <button
              type="button"
              className="btn-ghost"
              disabled={checking}
              onClick={() => void handleCheck()}
            >
              {checking ? "Checking…" : "Check for issues"}
            </button>
          </div>
          {analyzedFrom && (
            <p className="settings-label-hint">
              Fields populated from {analyzedFrom} — review before saving.{" "}
              Consider{" "}
              <a href="#" onClick={(e) => { e.preventDefault(); void handleCheck(); }}>
                checking for issues
              </a>{" "}
              before saving.
            </p>
          )}
        </header>

        {/* FEAT-052 / S-298 — read-only reverse lookup + delete. Reassigning
            a publication to a different profile stays in the Publications
            editor's existing picker; there's no detach-from-here action. */}
        <section className="voice-usedby-panel">
          <div className="voice-usedby-header">
            <h2 className="voice-preview-title">Used by</h2>
            <button
              type="button"
              className="btn-primary btn-danger"
              onClick={() => void handleDelete()}
              disabled={usedBy.length > 0 || deleting}
              title={usedBy.length > 0 ? "Reassign publications before deleting" : undefined}
            >
              {deleting ? "Deleting…" : "Delete voice profile"}
            </button>
          </div>
          {usedByQ.isLoading ? (
            <div className="state-loading">Loading…</div>
          ) : usedBy.length === 0 ? (
            <p className="voice-rules-empty">
              Not used by any publication — safe to delete.
            </p>
          ) : (
            <>
              <ul className="voice-rules-list">
                {usedBy.map((p) => (
                  <li key={p.id}>{p.name}</li>
                ))}
              </ul>
              <p className="settings-label-hint">
                Reassign these publications to a different voice profile
                before deleting.{" "}
                <Link to="/settings/publications">Manage in Publications →</Link>
              </p>
            </>
          )}
          {deleteError && <p className="settings-field-error">{deleteError}</p>}
        </section>

        <ValidationFindingsPanel findings={findings} loading={checking} error={checkError} />

        {analyzeOpen && (
          <AnalyzeVoiceDocumentModal
            onClose={() => setAnalyzeOpen(false)}
            onExtracted={applyAnalyzedFields}
          />
        )}

        <VoiceProfileFields form={form} setForm={setForm} />

        <div className="editor-save-bar">
          <div
            className={`editor-save-status ${
              dirty ? "is-dirty" : savedAt ? "is-saved" : ""
            }`}
          >
            {mutation.error
              ? `Save failed: ${mutation.error.message}`
              : dirty
                ? "Unsaved changes"
                : savedAt
                  ? `Saved ${savedAt.toLocaleTimeString()}`
                  : "Up to date"}
          </div>
          <div className="editor-save-actions">
            {recentlySaved && !dirty && !mutation.isPending && (
              <span className="editor-saved-badge" role="status">
                Saved
              </span>
            )}
            <button
              type="button"
              className="btn-primary"
              onClick={handleSave}
              disabled={!dirty || mutation.isPending}
            >
              {mutation.isPending ? "Saving…" : "Save voice profile"}
            </button>
          </div>
        </div>
      </div>
  );
}

// ---------- Standalone create (no publication involved) ----------

// FEAT-052 / S-299 — route: /settings/voice/new. Unlike the deleted
// publication-scoped create flow (FEAT-051 / S-297, which did a POST then
// a PATCH to attach), this is a single POST — the profile starts attached
// to nothing. Attachment happens afterward via the Publications editor's
// existing voice profile picker.
interface Agencyclient {
  id: number;
  name: string;
  slug: string;
}

const CREATE_WS_KEY = "ccm:voice-profiles:active_ws";

export function VoiceProfileCreateView() {
  const navigate = useNavigate();
  const api = useApi();

  const { data: workspaces, isLoading: wsLoading, error: wsError } =
    useSettingsWorkspaces<Agencyclient>();

  const [selectedWsId, setSelectedWsId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(CREATE_WS_KEY);
    return raw ? Number(raw) : null;
  });

  useEffect(() => {
    if (workspaces && workspaces.length > 0) {
      const stillValid = workspaces.some((w) => w.id === selectedWsId);
      if (!stillValid) setSelectedWsId(workspaces[0].id);
    }
  }, [workspaces, selectedWsId]);

  // Required, not optional: POST /voice-profiles is RLS-scoped to the
  // JWT's active agencyclient_id claim. Unlike the index (which the user
  // is already "in"), this is a fresh page and must activate explicitly.
  useEnsureActiveWorkspace(selectedWsId);

  const [form, setForm] = useState<FormState>(() => emptyForm(""));
  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const [analyzedFrom, setAnalyzedFrom] = useState<string | null>(null);

  function applyAnalyzedFields(fields: AnalyzedVoiceFields, sourceLabel: string) {
    setForm((f) => mergeAnalyzedFields(f, fields));
    setAnalyzedFrom(sourceLabel);
  }

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // FEAT-054 / S-305 — same shape as VoiceEditorBody's check state. Works
  // on this unsaved form exactly like it works on a saved profile.
  const [checking, setChecking] = useState(false);
  const [findings, setFindings] = useState<ValidationFinding[] | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);

  async function handleCheck() {
    if (checking) return;
    setCheckError(null);
    setChecking(true);
    try {
      setFindings(await checkForIssues(api, form));
    } catch (e) {
      setCheckError(checkErrorMessage(e));
    } finally {
      setChecking(false);
    }
  }

  const canCreate =
    selectedWsId != null &&
    form.name.trim().length > 0 &&
    form.coreIdentity.trim().length > 0;

  async function handleCreate() {
    if (!canCreate || creating) return;
    setError(null);
    setCreating(true);
    try {
      const created = await api<{ id: number }>("/voice-profiles", {
        method: "POST",
        body: JSON.stringify({
          agencyclient_id: selectedWsId,
          name: form.name.trim(),
          core_identity: form.coreIdentity,
          hard_rules: form.hardRules.filter((r) => r.trim().length > 0),
          strong_tendencies: form.strongTendencies.filter((r) => r.trim().length > 0),
          light_preferences: form.lightPreferences.filter((r) => r.trim().length > 0),
          anti_ai_patterns: form.antiAiPatterns.filter((r) => r.trim().length > 0),
          top_3_rules: form.top3Rules.filter((r) => r.trim().length > 0),
          litmus_test: form.litmusTest.trim() || undefined,
        }),
      });
      navigate(`/settings/voice/${created.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create voice profile.");
      setCreating(false);
    }
  }

  if (wsLoading) {
    return (
      <div className="settings-page">
        <div className="state-loading">Loading workspaces…</div>
      </div>
    );
  }

  if (wsError || !workspaces || workspaces.length === 0) {
    return (
      <div className="settings-page">
        <div className="state-error">
          {wsError instanceof ApiError && wsError.status === 403
            ? "You don't have permission to create voice profiles."
            : "Create a workspace under Settings → Workspaces before adding a voice profile."}
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page editor-page">
      <header className="editor-header">
        <Link to="/settings/voice" className="editor-back-link">
          ← Back to voice profiles
        </Link>
        <nav className="editor-breadcrumb" aria-label="Breadcrumb">
          <Link to="/settings/voice">Settings · Voice Profiles</Link>
          <span className="editor-breadcrumb-sep">/</span>
          <span>New voice profile</span>
        </nav>
        <h1 className="editor-title">New voice profile</h1>
        <p className="editor-sub">
          Analyze a document or fill in the fields below, then create it.
          You can attach it to a publication afterward from Settings → Publications.
        </p>

        {/* FEAT-052 / S-300 — Upload trigger sits parallel to the
            workspace picker, right-aligned, sized like a normal button —
            not stretched full-width under editor-header's flex-column. */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
          {workspaces.length > 1 && (
            <label className="settings-field" style={{ maxWidth: 320, marginBottom: 0 }}>
              <span className="settings-label">Workspace</span>
              <select
                className="settings-select"
                value={selectedWsId ?? workspaces[0].id}
                onChange={(e) => setSelectedWsId(Number(e.target.value))}
              >
                {workspaces.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name} ({w.slug})
                  </option>
                ))}
              </select>
            </label>
          )}
          <div style={{ display: "flex", gap: 12, marginLeft: "auto" }}>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setAnalyzeOpen(true)}
            >
              Upload a document to analyze
            </button>
            <button
              type="button"
              className="btn-ghost"
              disabled={checking}
              onClick={() => void handleCheck()}
            >
              {checking ? "Checking…" : "Check for issues"}
            </button>
          </div>
        </div>
        {analyzedFrom && (
          <p className="settings-label-hint">
            Fields populated from {analyzedFrom} — review before creating.{" "}
            Consider{" "}
            <a href="#" onClick={(e) => { e.preventDefault(); void handleCheck(); }}>
              checking for issues
            </a>{" "}
            before creating.
          </p>
        )}
      </header>

      {analyzeOpen && (
        <AnalyzeVoiceDocumentModal
          onClose={() => setAnalyzeOpen(false)}
          onExtracted={applyAnalyzedFields}
        />
      )}

      <ValidationFindingsPanel findings={findings} loading={checking} error={checkError} />

      <VoiceProfileFields form={form} setForm={setForm} />

      <div className="editor-save-bar">
        <div className={`editor-save-status ${error ? "is-dirty" : ""}`}>
          {error ? `Create failed: ${error}` : "Not yet created"}
        </div>
        <div className="editor-save-actions">
          <button
            type="button"
            className="btn-primary"
            onClick={() => void handleCreate()}
            disabled={!canCreate || creating}
          >
            {creating ? "Creating…" : "Create voice profile"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Validation findings panel (edit + create flows) ----------

const CATEGORY_LABELS: Record<ValidationFinding["category"], string> = {
  miscategorized: "Miscategorized",
  near_duplicate: "Near-duplicate",
  process_only: "Process, not output",
  system_redundant: "Redundant with a system setting",
};

// FEAT-054 / S-305 — renders nothing before the first check has been run,
// so it doesn't clutter the page by default. Advisory only: no action here
// writes back into the form: the founder edits fields manually based on
// what's shown, matching the review-before-save discipline established for
// the analyzer.
function ValidationFindingsPanel({
  findings,
  loading,
  error,
}: {
  findings: ValidationFinding[] | null;
  loading: boolean;
  error: string | null;
}) {
  if (!loading && !error && findings === null) return null;

  return (
    <section className="voice-usedby-panel">
      <div className="voice-usedby-header">
        <h2 className="voice-preview-title">Check for issues</h2>
      </div>
      {loading ? (
        <div className="state-loading">Checking…</div>
      ) : error ? (
        <p className="settings-field-error">{error}</p>
      ) : findings && findings.length === 0 ? (
        <p className="voice-rules-empty">No issues found.</p>
      ) : (
        <ul className="voice-rules-list">
          {(findings ?? []).map((f, i) => (
            <li key={i}>
              <strong>{CATEGORY_LABELS[f.category]}</strong> ({f.field}) — "{f.item}"
              <br />
              {f.explanation}
              {f.suggestion && (
                <>
                  <br />
                  <em>{f.suggestion}</em>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------- Shared field UI (edit + create flows) ----------

// FEAT-051 / S-297 — extracted so VoiceEditorBody (edit) and
// VoiceProfileCreateView (create, above) render identical field UI instead
// of drifting copies. Purely presentational — owns no state of its own.
interface VoiceProfileFieldsProps {
  form: FormState;
  setForm: Dispatch<SetStateAction<FormState>>;
}

function VoiceProfileFields({ form, setForm }: VoiceProfileFieldsProps) {
  return (
    <div className="voice-grid">
      <section className="voice-form">
        <label className="settings-field">
          <span className="settings-label">Name</span>
          <input
            className="settings-input"
            type="text"
            value={form.name}
            maxLength={120}
            onChange={(e) =>
              setForm((f) => ({ ...f, name: e.target.value }))
            }
          />
        </label>

        <label className="settings-field">
          <span className="settings-label">
            Core identity{" "}
            <span className="settings-label-hint">
              Markdown — rendered live in the preview
            </span>
          </span>
          <div className="voice-toolbar">
            <span>**bold** *italic* # heading - list</span>
            <span className="voice-toolbar-hint">
              {form.coreIdentity.length.toLocaleString()} chars
            </span>
          </div>
          <textarea
            className="voice-textarea"
            value={form.coreIdentity}
            onChange={(e) =>
              setForm((f) => ({ ...f, coreIdentity: e.target.value }))
            }
            spellCheck
            placeholder="Describe the voice — perspective, cadence, energy. Use markdown to add structure."
          />
        </label>

        {/* FEAT-049 / S-292 — Advanced: everything the pipeline actually
            reads besides Core Identity (Hard Rules, Strong Tendencies,
            Top 3 Rules), consolidated into one disclosure. Open by
            default if any of these already has content.
            FEAT-050 / S-294 — Tone Guide field removed; tone guidance
            now lives in the code-maintained TONE_REGISTRY instead. */}
        <details
          className="editor-disclosure"
          open={
            form.hardRules.length > 0 ||
            form.strongTendencies.length > 0 ||
            form.top3Rules.length > 0
          }
        >
          <summary className="editor-disclosure-summary">
            Advanced{" "}
            <span className="settings-label-hint">
              Fine-tunes the voice — hard rules and tendencies
            </span>
          </summary>
          <div className="editor-disclosure-body">
            <RuleListField
              label="Hard rules"
              hint="Non-negotiable. One rule per row. Each rule is capped at 500 chars."
              rules={form.hardRules}
              onChange={(next) =>
                setForm((f) => ({ ...f, hardRules: next }))
              }
              placeholder="No em dashes."
            />
            <RuleListField
              label="Strong tendencies"
              hint="Default behavior unless overridden by content type."
              rules={form.strongTendencies}
              onChange={(next) =>
                setForm((f) => ({ ...f, strongTendencies: next }))
              }
            />
            <RuleListField
              label="Top 3 rules"
              hint="Maximum 3. These are reinforced at every phase."
              rules={form.top3Rules}
              onChange={(next) =>
                setForm((f) => ({
                  ...f,
                  top3Rules: next.slice(0, 3),
                }))
              }
            />
          </div>
        </details>

        {/* FEAT-049 / S-292 — Reference only: fields the pipeline never
            reads. Kept reachable, clearly labeled, collapsed by default
            regardless of content (unlike Advanced, there's no "already
            in use" signal to auto-open on). */}
        <details className="editor-disclosure">
          <summary className="editor-disclosure-summary">
            Reference notes{" "}
            <span className="settings-label-hint">
              Not used in generation — for your own reference
            </span>
          </summary>
          <div className="editor-disclosure-body">
            <label className="settings-field">
              <span className="settings-label">
                Litmus test{" "}
                <span className="settings-label-hint">
                  Optional — short prompt used to gut-check tone
                </span>
              </span>
              <textarea
                className="voice-textarea voice-textarea-short"
                value={form.litmusTest}
                onChange={(e) =>
                  setForm((f) => ({ ...f, litmusTest: e.target.value }))
                }
                placeholder="Does this sound like our team in the company Slack?"
              />
            </label>
            <RuleListField
              label="Light preferences"
              hint="Nudges the agent can break with reason."
              rules={form.lightPreferences}
              onChange={(next) =>
                setForm((f) => ({ ...f, lightPreferences: next }))
              }
            />
            <RuleListField
              label="Anti-AI patterns"
              hint="Phrases / shapes the agent must avoid."
              rules={form.antiAiPatterns}
              onChange={(next) =>
                setForm((f) => ({ ...f, antiAiPatterns: next }))
              }
            />
          </div>
        </details>

        {/* FEAT-008 / S-121 — Source Markdown: read-mostly, collapsed by
            default. Shows the original VOICE_PROFILE.md verbatim so the
            operator can reference it while refining the structured fields.
            Gated to admin (all users who can reach this editor are already
            admin; kept as a collapsed disclosure so it doesn't dominate). */}
        <details className="editor-disclosure">
          <summary className="editor-disclosure-summary">
            Source markdown{" "}
            <span className="settings-label-hint">
              Original imported content — reference while editing structured fields
            </span>
          </summary>
          <div className="editor-disclosure-body">
            <textarea
              className="voice-textarea voice-textarea-source"
              rows={12}
              value={form.sourceMarkdown}
              onChange={(e) =>
                setForm((f) => ({ ...f, sourceMarkdown: e.target.value }))
              }
              placeholder="Paste or import VOICE_PROFILE.md verbatim content here."
            />
          </div>
        </details>
      </section>

      <section className="voice-preview">
        <h2 className="voice-preview-title">Preview</h2>
        {form.coreIdentity.trim().length === 0 ? (
          <div className="voice-preview-empty">
            Start typing to see the rendered voice profile.
          </div>
        ) : (
          <div
            className="voice-preview-body panel-md"
            dangerouslySetInnerHTML={{
              __html: markdownToHtml(form.coreIdentity),
            }}
          />
        )}

        <div>
          <h3 className="voice-preview-title">Hard rules</h3>
          {form.hardRules.filter((r) => r.trim().length > 0).length ===
          0 ? (
            <div className="voice-rules-empty">
              No hard rules yet. Add the non-negotiables in the editor.
            </div>
          ) : (
            <ul className="voice-rules-list">
              {form.hardRules
                .filter((r) => r.trim().length > 0)
                .map((rule, i) => (
                  <li key={i}>{rule}</li>
                ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

// ---------- Rule list editor ----------

interface RuleListFieldProps {
  label: string;
  hint?: string;
  rules: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}

function RuleListField({
  label,
  hint,
  rules,
  onChange,
  placeholder,
}: RuleListFieldProps) {
  return (
    <div className="settings-field">
      <span className="settings-label">
        {label}
        {hint && <span className="settings-label-hint">{hint}</span>}
      </span>
      <div className="rule-list">
        {rules.length === 0 && (
          <span className="voice-rules-empty">No rules yet.</span>
        )}
        {rules.map((rule, i) => (
          <div className="rule-row" key={i}>
            <input
              className="settings-input"
              type="text"
              value={rule}
              maxLength={500}
              placeholder={placeholder}
              onChange={(e) => {
                const next = rules.slice();
                next[i] = e.target.value;
                onChange(next);
              }}
            />
            <button
              type="button"
              className="rule-remove-btn"
              aria-label={`Remove rule ${i + 1}`}
              title="Remove rule"
              onClick={() => onChange(rules.filter((_, j) => j !== i))}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn-ghost rule-add-btn"
          onClick={() => onChange([...rules, ""])}
        >
          + Add rule
        </button>
      </div>
    </div>
  );
}
